/**
 * Custom hooks for accessing regional features and step configurations
 */

import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useLoadSpacingScenario} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacingScenario'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {useMemo} from 'react'

// Import types from non-circular dependencies
import {RegionFeatures} from '../../../orderIntake/config/regionFeatures'
import {DEFAULT_STEP_ORDER} from '../../../orderIntake/config/stepConfig'
import {RmcOrderIntakeStepType} from '../../../orderIntake/config/stepTypes'
import {useOrderIntakeFeatureToggles} from '../../../orderIntake/hooks/useOrderIntakeFeatureToggles'

import {useRmcCountryFeatures} from './useRmcCountryFeatures'

// Define the StepConfig type here
export type StepConfig = {
  name: RmcOrderIntakeStepType
  isEnabled: boolean
  component: string
  fields?: string[]
  order: number
}

export type FieldConfig = {
  name: string
  isEnabled: boolean
}

/**
 * Hook to access region-specific features
 * This uses the useRmcCountryFeatures hook which fetches features from a remote config
 */
export const useRegionFeatures = (): RegionFeatures => {
  return useRmcCountryFeatures()
}

/**
 * Hook to get dynamic step configurations based on region features and form state
 */
export const useStepConfigurations = (): StepConfig[] => {
  const regionFeatures = useRegionFeatures()
  const {getValues} = useOrderProcessFormContext()
  const loadSpacingScenario = useLoadSpacingScenario()
  const {enableOrderIntakeContractSelection} = useOrderIntakeFeatureToggles()

  // Get contracts from configuration to determine if quote selection should be enabled
  const mixConfiguration = useFieldConfigurationContext('mix')
  const hasMultipleContracts = (mixConfiguration?.contracts?.length ?? 0) > 1

  // Get material variations configuration to determine if it should be enabled
  const materialVariationsConfiguration = useFieldConfigurationContext('mixCharacteristics')
  const isMaterialVariationsEnabled = materialVariationsConfiguration?.isEnabled ?? false

  // Get relevant form values for conditional logic
  const quantitySelection = getValues('quantity')
  const hasQuantityValue =
    quantitySelection?.type === 'fullLoads'
      ? (quantitySelection.fullLoads ?? 0) >= 1
      : Boolean(quantitySelection?.volume) && Number(quantitySelection?.volume) > 0

  return useMemo(() => {
    // Define step configurations with dynamic enablement logic
    const configs: StepConfig[] = [
      {
        name: RmcOrderIntakeStepType.QUOTE,
        // Quote step enabled only if feature toggle is on AND there are multiple contracts
        isEnabled: enableOrderIntakeContractSelection && hasMultipleContracts,
        component: 'QuoteStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.QUOTE]
      },
      {
        name: RmcOrderIntakeStepType.MATERIAL,
        isEnabled: true, // Material step always enabled
        component: 'MaterialStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.MATERIAL]
      },
      {
        name: RmcOrderIntakeStepType.MIX_CHARACTERISTICS,
        isEnabled: isMaterialVariationsEnabled, // Enabled when any materials have characteristics available
        component: 'MaterialVariationsStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.MIX_CHARACTERISTICS]
      },
      {
        name: RmcOrderIntakeStepType.QUANTITY,
        isEnabled: true, // Quantity step always enabled
        component: 'QuantityStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.QUANTITY]
      },
      {
        name: RmcOrderIntakeStepType.USAGE,
        isEnabled: regionFeatures.hasConcreteUsage,
        component: 'UsageStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.USAGE]
      },
      {
        name: RmcOrderIntakeStepType.UNLOADING_METHOD,
        isEnabled: regionFeatures.hasUnloadingMethod,
        component: 'UnloadingMethodStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.UNLOADING_METHOD]
      },
      {
        name: RmcOrderIntakeStepType.INTERVALS,
        // Enable based on quantity/fullLoads and scenario settings
        isEnabled:
          hasQuantityValue &&
          (loadSpacingScenario.loadSpace ||
            regionFeatures.allowTimeOnJobSite ||
            regionFeatures.allowFlowRate),
        component: 'IntervalsStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.INTERVALS]
      },
      {
        name: RmcOrderIntakeStepType.DATE,
        isEnabled: true, // Date always required
        component: 'DateStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.DATE]
      },
      {
        name: RmcOrderIntakeStepType.TIME,
        isEnabled: true, // Time always required
        component: 'TimeStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.TIME]
      },
      {
        name: RmcOrderIntakeStepType.DETAILS,
        isEnabled: true, // Details step always enabled
        component: 'DetailsStep',
        order: DEFAULT_STEP_ORDER[RmcOrderIntakeStepType.DETAILS],
        // Define fields that should appear in details step
        fields: getDetailScreenFields(regionFeatures)
      }
    ]

    return configs
  }, [
    regionFeatures,
    hasQuantityValue,
    loadSpacingScenario,
    enableOrderIntakeContractSelection,
    hasMultipleContracts,
    isMaterialVariationsEnabled
  ])
}

/**
 * Helper function to determine which fields should be shown in the details screen
 */
const getDetailScreenFields = (regionFeatures: RegionFeatures): string[] => {
  const fields = ['orderPlacer', 'siteContact', 'poNumber'] // Base fields

  // Add conditional fields based on region features
  if (regionFeatures.hasAdmixtures) fields.push('retarder')
  if (regionFeatures.hasTesting) fields.push('testing')
  if (regionFeatures.hasDriverInstructions) fields.push('driverInstructions')
  if (regionFeatures.hasNotes) fields.push('additionalNotes')

  return fields
}

export const useDetailsScreenFields = (): string[] => {
  const regionFeatures = useRegionFeatures()

  return useMemo(() => {
    return getDetailScreenFields(regionFeatures)
  }, [regionFeatures])
}

export const useFieldConfigurations = (): FieldConfig[] => {
  const regionFeatures = useRegionFeatures()

  return useMemo(() => {
    const configs: FieldConfig[] = [
      {
        name: 'retarder',
        isEnabled: regionFeatures.hasAdmixtures
      },
      {
        name: 'po_number',
        isEnabled: true
      },
      {
        name: 'driver_instructions',
        isEnabled: regionFeatures.hasDriverInstructions
      },
      {
        name: 'additional_notes',
        isEnabled: regionFeatures.hasNotes
      }
    ]

    return configs
  }, [regionFeatures])
}
